import React, { useState } from "react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

interface PaginationProps {
  totalPages: number; // Total number of pages
  onPageChange: (page: number) => void; // Function to handle page changes
}

const Pagination: React.FC<PaginationProps> = ({ totalPages, onPageChange }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Handle previous button click
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      onPageChange(prevPage);
    }
  };

  // Handle next button click
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      onPageChange(nextPage);
    }
  };

  // Go to specific page
  const goToPage = (page: number) => {
    setCurrentPage(page);
    onPageChange(page);
  };

  // Render page numbers
  const renderPageNumbers = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => (
        <button
          key={i + 1}
          type="button"
          className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none ${
            currentPage === i + 1
              ? "bg-blue-50 text-secondary focus:bg-gray-300"
              : "text-gray-800 hover:bg-gray-100"
          }`}
          onClick={() => goToPage(i + 1)}
        >
          {i + 1}
        </button>
      ));
    }
  
    let pages: JSX.Element[] = [];
  
    // Always include page 1
    pages.push(
      <button
        key={1}
        type="button"
        className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none ${
          currentPage === 1
            ? "bg-blue-50 text-secondary focus:bg-gray-300"
            : "text-gray-800 hover:bg-gray-100"
        }`}
        onClick={() => goToPage(1)}
      >
        1
      </button>
    );
  
    // Case 1: If currentPage is close to the start (1, 2, 3)
    if (currentPage <= 3) {
      for (let i = 2; i <= 3; i++) {
        pages.push(
          <button
            key={i}
            type="button"
            className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none ${
              currentPage === i
                ? "bg-blue-50 text-secondary focus:bg-gray-300"
                : "text-gray-800 hover:bg-gray-100"
            }`}
            onClick={() => goToPage(i)}
          >
            {i}
          </button>
        );
      }
      pages.push(<span key="dots-right">...</span>);
    }
  
    // Case 2: If currentPage is near the end
    else if (currentPage >= totalPages - 2) {
      pages.push(<span key="dots-left">...</span>);
      for (let i = totalPages - 2; i < totalPages; i++) {
        pages.push(
          <button
            key={i}
            type="button"
            className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none ${
              currentPage === i
                ? "bg-blue-50 text-secondary focus:bg-gray-300"
                : "text-gray-800 hover:bg-gray-100"
            }`}
            onClick={() => goToPage(i)}
          >
            {i}
          </button>
        );
      }
    }
  
    // Case 3: General case (middle pages)
    else {
      pages.push(<span key="dots-left">...</span>);
      pages.push(
        <button
          key={currentPage - 1}
          type="button"
          className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none text-gray-800 hover:bg-gray-100"`}
          onClick={() => goToPage(currentPage - 1)}
        >
          {currentPage - 1}
        </button>
      );
      pages.push(
        <button
          key={currentPage}
          type="button"
          className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none bg-blue-50 text-secondary focus:bg-gray-300`}
        >
          {currentPage}
        </button>
      );
      pages.push(
        <button
          key={currentPage + 1}
          type="button"
          className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none text-gray-800 hover:bg-gray-100"`}
          onClick={() => goToPage(currentPage + 1)}
        >
          {currentPage + 1}
        </button>
      );
      pages.push(<span key="dots-right">...</span>);
    }
  
    // Always include last page
    pages.push(
      <button
        key={totalPages}
        type="button"
        className={`min-h-[38px] min-w-[38px] flex justify-center items-center py-2 px-3 text-sm rounded-lg focus:outline-none ${
          currentPage === totalPages
            ? "bg-blue-50 text-secondary focus:bg-gray-300"
            : "text-gray-800 hover:bg-gray-100"
        }`}
        onClick={() => goToPage(totalPages)}
      >
        {totalPages}
      </button>
    );
  
    return pages;
  };

  return (
    <nav className="flex justify-end items-center gap-x-4" aria-label="Pagination">
      {/* Previous Button */}
      <button
        type="button"
        className="min-h-[38px] min-w-24 py-2 bg-white border border-borderGray px-3 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg  disabled:opacity-50 disabled:pointer-events-none"
        aria-label="Previous"
        onClick={goToPreviousPage}
        disabled={currentPage === 1}
      >
        <FiArrowLeft />
        <span>Previous</span>
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-x-1">{renderPageNumbers()}</div>

      {/* Next Button */}
      <button
        type="button"
        className="min-h-[38px] min-w-24 py-2 bg-white border border-borderGray px-3 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg  disabled:opacity-50 disabled:pointer-events-none"
        aria-label="Next"
        onClick={goToNextPage}
        disabled={currentPage === totalPages}
      >
        <span>Next</span>
        <FiArrowRight />
      </button>
    </nav>
  );
};

export default Pagination;
